import React from 'react';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import NumberFormat from 'react-number-format';
import { IUser, IUserStats } from '@pvplevelup/core/dist/interfaces/IUser';
import { Grid, Paper, Box, colors, Stepper, Step, StepLabel, LinearProgress, useMediaQuery } from '@material-ui/core';
import { CircularProgressWithLabel } from 'components/Progress/CircularWithLabel';
import { normalize } from 'utilities/normalize'
import ReactResizeDetector from 'react-resize-detector';
import {ExpandLess,ExpandMore, Remove, EmojiEvents, ShowChart} from '@material-ui/icons'
import { Sword } from 'components/SvgIcons/Sword';
import { Cross } from 'components/SvgIcons/Cross';
import { Crosshair } from 'components/SvgIcons/Crosshair';
import { IGameFeed } from '@pvplevelup/core/dist/queries/game/gameFeed';
import { IUserMapFeed } from '@pvplevelup/core/dist/queries/user/user';
import { MapThumbnailIcons } from 'utilities/map_icons';
import { ZoneInformation } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { hhmmss } from '@pvplevelup/core/dist/utility/time';
import { classImages } from 'utilities/ClassImages';
import { Link, NavLink } from 'react-router-dom';


interface Props {
  user: IUser | null;
  stats: IUserStats | undefined;
  classStats: IUserStats | undefined;
}

interface DiffProps{
  stat: number | undefined;
  classStat: number | undefined;
  fontSize?: string;
  iconSize?: string;
  precision?: number;
  isTime?: boolean;
}


const useStyles = makeStyles((theme) => {
	return {
    stepper:{
      background: 'none'
    },
    progress:{
      height:10
    },
    secondBar:{
      height:10,
      backgroundColor:colors.red[900]
    },
    userlink:{
      fontSize: '1.25em',
      fontWeight: 600,
      textDecoration: "none",
      color: "#ffffff",
      "&:hover,&:focus": {
        color: "#144193"
      }
    }
	};
});

function round(value:number, decimals:number) {
  //@ts-ignore
  return Number(Math.round(value+'e'+decimals)+'e-'+decimals);
}

export const largeScreenOffset = 248;
export const smallScreenOffset = 325;
export const breakPoint = 1250;

export const DifferenceComponent: React.FC<DiffProps> = ({stat,classStat,fontSize,precision,iconSize,isTime}) => {
  let diff:any = round((stat || 0) - (classStat || 0),(precision == undefined) ? 2 : precision);
  let text = diff;
  if(isTime)
    text = hhmmss(Math.abs(diff),false);
  return <div style={{fontSize:fontSize ? fontSize : '1em',color:(diff > 0) ? colors.blue[600] : (diff < 0) ? colors.orange[800] : 'initial'}}>{diff > 0 ? <ExpandLess style={{fontSize:iconSize ? iconSize : '1.2em'}} /> : diff < 0 ? <ExpandMore  style={{fontSize:iconSize ? iconSize : '1.2em'}} /> : <Remove  style={{fontSize:iconSize ? iconSize : '1.2em'}} />} {isTime ? text : <NumberFormat value={text} thousandSeparator={true} decimalScale={(precision == undefined) ? 2 : precision} displayType="text" />}</div>
}

let fontColor = '#A6B0BD';
let borderBottom = "1px dotted #484E5B";

const UserCharacters: React.FC<Props> = ({user,stats,classStats}) => {
  const classes = useStyles();

  const matches = useMediaQuery(`(max-width:${breakPoint}px)`);
 
	return (
    <div className="user-stats-grid" style={{minHeight:"100%"}}>
      <Grid container spacing={2} alignItems={"baseline"}>

        {stats?.characters?.map((characterStat,index)=>{
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={characterStat.character?._id}>
              <Paper>
                <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto auto auto"} minHeight={348}>
                  <Box paddingBottom="1em"><NavLink className={classes.userlink} to={`/characters/${characterStat?.character?._id}`}>{characterStat.character?.name}</NavLink></Box>
                  <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                    <img src={classImages.getColorIcon(characterStat.character?.class || "")} style={{height:'80px',width:'80px'}} title={characterStat.character?.class} />
                    <Box>
                      <div style={{fontSize:'2em'}}>{((characterStat?.wr || 0) * 100).toFixed(2)}%</div>
                      <div style={{fontWeight:'initial'}}><NumberFormat value={(characterStat?.wins || 0) + (characterStat?.loses || 0) + (characterStat?.ties || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /> Played</div>
                      <div style={{fontWeight:'initial'}}><NumberFormat value={(characterStat?.wins || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />-<NumberFormat value={(characterStat?.loses || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />-<NumberFormat value={(characterStat?.ties || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></div>
                    </Box>
                  </Box>
                  <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                    <Box fontWeight="bold">KDA / Avg KDA / Top KDA</Box>
                    <Box justifySelf="end">
                      <NumberFormat value={(characterStat?.kda || 0)} thousandSeparator={true} decimalScale={2} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.avg_kda || 0)} thousandSeparator={true} decimalScale={2} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.top_kda || 0)} thousandSeparator={true} decimalScale={2} displayType="text" />
                    </Box>
                  </Box>
                  <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                    <Box fontWeight="bold">Avg Peak Burst / Burst</Box>
                    <Box justifySelf="end">
                      <NumberFormat value={(characterStat?.avg_peakburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.avg_avgburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    </Box>
                  </Box>
                  <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                    <Box fontWeight="bold">Top Peak Burst / Burst</Box>
                    <Box justifySelf="end">
                      <NumberFormat value={(characterStat?.top_peakburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.top_avgburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    </Box>
                  </Box>
                  <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                    <Box fontWeight="bold">Avg CC / Off. CC / Def. CC</Box>
                    <Box justifySelf="end">
                      <NumberFormat value={(characterStat?.avg_ccs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.avg_oCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.avg_dCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    </Box>
                  </Box>
                  <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                    <Box fontWeight="bold">Top CC / Off. CC / Def. CC</Box>
                    <Box justifySelf="end">
                      <NumberFormat value={(characterStat?.top_ccs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.top_oCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.top_dCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    </Box>
                  </Box>
                  <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                    <Box fontWeight="bold">Avg HPS / eBig</Box>
                    <Box justifySelf="end">
                      <NumberFormat value={(characterStat?.avg_hps || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />{" / "}
                      <NumberFormat value={(characterStat?.avg_eBigHeal || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    </Box>
                  </Box>
                  <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                    <Box fontWeight="bold">TIME IN OBJECTIVE</Box>
                    <Box justifySelf="end" textAlign="end">
                      {hhmmss(Math.round(characterStat?.avg_timeInObjective || 0),false)}
                      <DifferenceComponent isTime={true} stat={Math.round(characterStat?.avg_timeInObjective || 0)} classStat={Math.round(classStats?.avg_timeInObjective || 0)} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )
        })}
      </Grid>
    </div>
	);
};

export {UserCharacters};